import React, { Component, createRef } from "react";
import PropTypes from "prop-types";

import { gatherClasses } from "../../lib/utils";
import {
  getWidth,
  requestAnimationFramePolyfill,
  cancelAnimationFramePolyfill
} from "./sliderUtils";
import Tooltip from "./Tooltip";

class Handle extends Component {
  isDraggingStatus = false;

  animationFrameID = null;

  domNode;

  boundMoveEvent = false;

  boundUpEvent = false;

  moveEvent = null;

  deltaX = 0;

  deltaY = 0;

  x = 0;

  y = 0;

  isMouseMoveTrackerSet = false;

  constructor(props) {
    super(props);
    this.tooltipRef = createRef();
    this.state = {
      active: false
    };
  }

  componentWillUnmount() {
    this.releaseMouseMoves();
  }

  onMouseMove = event => {
    const x = event.clientX;
    const y = event.clientY;

    this.deltaX += x - this.x;
    this.deltaY += y - this.y;

    if (this.animationFrameID === null) {
      this.animationFrameID = requestAnimationFramePolyfill(this.didMouseMove);
    }

    this.x = x;
    this.y = y;

    this.moveEvent = event;
    event.preventDefault();
  };

  onMouseUp = () => {
    if (this.animationFrameID) {
      this.didMouseMove();
    }

    if (this.handleDragEnd) {
      this.handleDragEnd();
    }
  };

  setTooltipPosition() {
    const { tooltip } = this.props;
    const tooltipElement = this.tooltipRef.current;

    if (tooltip && tooltipElement) {
      const width = getWidth(tooltipElement);
      tooltipElement.style.left = `-${width / 2}px`;
    }
  }

  didMouseMove = () => {
    this.animationFrameID = null;
    this.handleDragMove(this.deltaX, this.deltaY, this.moveEvent);

    this.deltaX = 0;
    this.deltaY = 0;
  };

  isDragging = () => this.isDraggingStatus;

  releaseMouseMoves = () => {
    if (this.isMouseMoveTrackerSet) {
      if (this.boundMoveEvent) {
        this.domNode.removeEventListener("mousemove", this.onMouseMove);
        this.boundMoveEvent = false;
      }

      if (this.boundUpEvent) {
        this.domNode.removeEventListener("mouseup", this.onMouseUp);
        this.boundUpEvent = false;
      }

      if (this.animationFrameID !== null) {
        cancelAnimationFramePolyfill(this.animationFrameID);
        this.animationFrameID = null;
      }

      if (this.isDraggingStatus) {
        this.isDraggingStatus = false;
        this.x = 0;
        this.y = 0;
      }

      this.isMouseMoveTrackerSet = false;
    }
  };

  handleDragMove = (_deltaX, _deltaY, event) => {
    const { onDragMove } = this.props;
    if (!this.isMouseMoveTrackerSet || !this.isDragging()) {
      return;
    }

    if (onDragMove) {
      onDragMove(event);
    }
    this.setTooltipPosition();
  };

  handleDragEnd = event => {
    const { onDragEnd } = this.props;

    this.releaseMouseMoves();
    this.setState({
      active: false
    });

    if (onDragEnd) {
      onDragEnd(event);
    }
  };

  handleMouseDown = event => {
    const { disabled } = this.props;

    if (disabled) {
      return;
    }

    this.domNode = document.body;

    this.isMouseMoveTrackerSet = true;

    this.captureMouseMoves(event);
    this.setState({
      active: true
    });
  };

  handleMouseEnter = () => {
    this.setTooltipPosition();
  };

  captureMouseMoves(event) {
    if (!this.boundMoveEvent && !this.boundUpEvent) {
      this.domNode.addEventListener("mousemove", this.onMouseMove);
      this.domNode.addEventListener("mouseup", this.onMouseUp);
      this.boundMoveEvent = true;
      this.boundUpEvent = true;
    }

    if (!this.isDraggingStatus) {
      this.deltaX = 0;
      this.deltaY = 0;
      this.isDraggingStatus = true;
      this.x = event.clientX;
      this.y = event.clientY;
    }

    event.preventDefault();
  }

  render() {
    const { position, tooltip, value } = this.props;
    const { active } = this.state;

    const styles = {
      left: `${position}%`
    };

    return (
      <div
        className={gatherClasses(
          null,
          "oui-slider-handle",
          active ? "oui-active" : ""
        )}
        role="presentation"
        onMouseDown={this.handleMouseDown}
        onMouseEnter={this.handleMouseEnter}
        style={styles}
      >
        {tooltip && (
          <Tooltip
            htmlElementRef={this.tooltipRef}
            className="oui-tooltip oui-placement-top"
          >
            {value}
          </Tooltip>
        )}
      </div>
    );
  }
}

Handle.propTypes = {
  disabled: PropTypes.bool,
  onDragEnd: PropTypes.func,
  onDragMove: PropTypes.func,
  position: PropTypes.number.isRequired,
  tooltip: PropTypes.bool,
  value: PropTypes.number.isRequired
};

Handle.defaultProps = {
  disabled: false,
  onDragEnd: null,
  onDragMove: null,
  tooltip: false
};

Handle.displayName = "OrionSliderHandle";

export default Handle;
